import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { QuizPuzzleConfig } from '../interfaces/QuizPuzzleConfig';
import { transition, trigger, useAnimation } from '@angular/animations';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { Store } from '@ngxs/store';
import { Md5 } from 'ts-md5/dist/md5';
import { QuizGameState } from '../store/quiz-game.state';
import { QuizGameStateModel } from '../store/quiz-game-state.model';
import { InitQuizGame, PersistQuizUserInput, SetCurrentQuestion } from '../store/quiz-game.actions';
import { fadeIn, fadeOut, flipIn, jackIn, scaleIn, scaleOut } from './quiz-board.animations';
import { Button } from '../../shared-module/buttons/button.def';
import { PuzzleGameModel } from '../../store/puzzle-game.model';
import { SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { PuzzleWinDialogComponent } from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.component';
import { QuizWinScreenComponent } from '../win-screen/quiz-win-screen.component';
import { Subscription } from 'rxjs';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { QuizHelpScreenComponent } from '../help-screen/quiz-help-screen.component';
import {
  ModalController,
  IonButton,
  IonButtons,
  IonCard,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonLabel,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { ResizeObserver } from '@juggle/resize-observer';
import { TranslateModule } from '@ngx-translate/core';
import { arrowBackSharp, arrowForwardSharp, playForwardSharp } from 'ionicons/icons';
import { addIcons } from 'ionicons';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';

@Component({
  selector: 'lib-app-quiz-board',
  templateUrl: './quiz-board.component.html',
  styleUrls: ['./quiz-board.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonButton,
    IonButtons,
    IonCard,
    IonCol,
    IonContent,
    IonGrid,
    IonIcon,
    IonLabel,
    IonRow,
    IonTitle,
    IonToolbar,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideAnimation', [
      /* scale */
      transition('void => scale', [useAnimation(scaleIn, { params: { time: '500ms' } })]),
      transition('scale => void', [useAnimation(scaleOut, { params: { time: '500ms' } })]),

      /* fade */
      transition('void => fade', [useAnimation(fadeIn, { params: { time: '500ms' } })]),
      transition('fade => void', [useAnimation(fadeOut, { params: { time: '500ms' } })]),

      /* flip */
      transition('void => flip', [useAnimation(flipIn, { params: { time: '500ms' } })]),
      transition('flip => void', [useAnimation(scaleOut, { params: { time: '500ms' } })]),

      /* JackInTheBox */
      transition('void => jackInTheBox', [useAnimation(jackIn, { params: { time: '700ms' } })]),
      transition('jackInTheBox => void', [useAnimation(scaleOut, { params: { time: '500ms' } })]),
    ]),
  ],
})
export class QuizBoardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('questionIndicator', { static: false })
  questionIndicator: ElementRef<HTMLElement>;

  puzzleConfig: QuizPuzzleConfig;
  // currentQuestion = 0;
  quizGameState: QuizGameStateModel;

  private puzzleID: string;
  private puzzleGame: PuzzleGameModel;
  public paused: boolean;

  public nextUnsolvedIndex = 0;

  private subscriptions: Subscription[] = [];
  public scaleFactor = 1;
  flipped = false;
  initialized = false;

  constructor(
    private el: ElementRef,
    private cd: ChangeDetectorRef,
    private framework: PuzzleFrameworkService,
    private store: Store,
    private winDialogService: PuzzleWinDialogService,
    private quizBoard: ElementRef,
    private helpDialogService: PuzzleHelpDialogService,
    private ionicModal: ModalController,
    private settingsModalService: PuzzleSettingsModalService
  ) {
    addIcons({
      arrowBackSharp,
      arrowForwardSharp,
      playForwardSharp,
    });
    // window.setInterval(() => {
    //   this.store.dispatch(new SetCurrentQuestion({ puzzleID: this.puzzleID, currentQuestion: 3 }));
    //
    //   this.cd.detectChanges();
    // }, 2000);

    this.subscriptions.push(
      this.winDialogService.winDialogRegistered.subscribe(() => {
        this.winDialogService.showButtons([
          PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
          PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
        ]);
        this.winDialogService.injectComponent(QuizWinScreenComponent, { quizConfig: this.puzzleConfig });
      })
    );
    this.subscriptions.push(
      this.helpDialogService.helpDialogRegistered.subscribe(() => {
        this.helpDialogService.injectHelpComponent(QuizHelpScreenComponent, {});
      })
    );
    this.subscriptions.push(
      this.framework.buttonPressed.subscribe((button) => {
        if (button === Button.Reset) {
          const userInput = this.puzzleConfig.questions.map(() => undefined);
          this.store.dispatch(new PersistQuizUserInput({ puzzleID: this.puzzleID, userInput }));
          this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.QuizGame, 0);
          this.store.dispatch(new SetCurrentQuestion({ puzzleID: this.puzzleID, currentQuestion: 0 }));
        }
      })
    );

    this.framework.puzzleEvents.subscribe((event) => {
      switch (event) {
        case PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE:
          this._winGame();
          break;
      }
    });
  }

  // @HostListener('window:resize')
  onResize() {
    if (!this.questionIndicator) {
      return;
    }

    const availableSpace = this.quizBoard.nativeElement.offsetWidth;
    const requiredSpace = this.questionIndicator.nativeElement.offsetWidth + 70;

    if (requiredSpace < availableSpace) {
      this.scaleFactor = 1;
      return;
    }

    this.scaleFactor = availableSpace / requiredSpace;
  }

  ngOnDestroy(): void {
    this.subscriptions.map((subcription) => subcription.unsubscribe());
    this.subscriptions = [];
  }

  ngOnInit(): void {
    this.puzzleConfig = this.framework.puzzleConfig;

    const puzzleID = this.puzzleConfig.puzzleID;

    this.puzzleID = puzzleID;

    this.store.dispatch(new SetPuzzleID({ puzzleID }));

    this.subscriptions.push(
      this.store
        .select((state) => state.puzzleGameState.paused)
        .subscribe((paused) => {
          this.paused = !!paused;
          window.setTimeout(() => {
            this.onResize();
            this.cd.detectChanges();
          }, 0);
        })
    );

    this.subscriptions.push(
      this.store.select(PuzzleGameState.getByID(puzzleID)).subscribe((puzzleGame: PuzzleGameModel) => {
        if (puzzleGame === null) {
          this.framework.initPuzzleGameState(puzzleID, PuzzleGameTypesEnum.QuizGame, 0);
          return;
        }
        this.puzzleGame = puzzleGame;
        // this.cd.detectChanges();
      })
    );

    this.subscriptions.push(
      this.store.select(QuizGameState.getByID(puzzleID)).subscribe((quizGame: QuizGameStateModel) => {
        if (quizGame === null) {
          this.initQuizGame();
          return;
        }
        this.quizGameState = quizGame;
        const emptyQuestions = this.quizGameState.userInput
          .map((str, index) => ({ str, index }))
          .filter((element) => !element.str);

        this.nextUnsolvedIndex = emptyQuestions && emptyQuestions.length ? emptyQuestions[0].index : -1;
        // this.cd.detectChanges();
        window.setTimeout(() => {
          this.onResize();
          this.initialized = true;
        }, 0);
      })
    );

    const observer = new ResizeObserver((entries) => {
      this.onResize();
    });
    observer.observe(this.el.nativeElement);
  }

  initQuizGame(): void {
    const roundHashes = this.puzzleConfig.questions.map((question) =>
      question.answers.map((answer) => Md5.hashStr(question.question + answer) as string)
    );

    const userInput = this.puzzleConfig.questions.map(() => undefined);

    this.store.dispatch(new InitQuizGame({ puzzleID: this.puzzleID, roundHashes, userInput }));
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  async selectAnswer(answerIndex: number) {
    if (this.quizGameState.userInput[this.quizGameState.currentQuestion]) {
      return;
    }
    const userInput = JSON.parse(JSON.stringify(this.quizGameState.userInput));
    userInput[this.quizGameState.currentQuestion] = this.quizGameState.roundHashes[this.quizGameState.currentQuestion][
      answerIndex
    ] as string;
    const answered = userInput.filter((str) => !!str).length;
    this.store.dispatch(new SetGameProgress({ puzzleID: this.puzzleID, progress: answered / userInput.length }));
    await this.store.dispatch(new PersistQuizUserInput({ puzzleID: this.puzzleID, userInput }));
  }

  getButtonColor(questionIndex: number, answerIndex: number) {
    // {'correct': !!quizGameState.userInput[i] && question.solution === quizGameState.roundHashes[i][j]}

    // Question has not been answered
    if (!this.quizGameState.userInput[questionIndex]) {
      return '';
    }

    const buttonHash = this.quizGameState.roundHashes[questionIndex][answerIndex];
    const question = this.puzzleConfig.questions[questionIndex];
    const userInput = this.quizGameState.userInput[questionIndex];

    // Question answered, this answer was correctly selected
    if (question.solution === this.quizGameState.roundHashes[questionIndex][answerIndex]) {
      return 'answered-correct';
    }

    // Question answered, this answer was incorrectly selected
    if (userInput !== question.solution && userInput === buttonHash) {
      return 'answered-incorrect';
    }

    // Question answered, but this answer is not selected
    return 'answered-not-selected';
  }

  async prevQuestion() {
    this.store.dispatch(
      new SetCurrentQuestion({ puzzleID: this.puzzleID, currentQuestion: this.quizGameState.currentQuestion - 1 })
    );
  }

  async nextQuestion() {
    this.store.dispatch(
      new SetCurrentQuestion({ puzzleID: this.puzzleID, currentQuestion: this.quizGameState.currentQuestion + 1 })
    );
  }

  getIndicatorClasses(questionIndex: number): any {
    const currentQuestion = this.quizGameState.currentQuestion;
    const classes: any = {
      current: questionIndex === this.quizGameState.currentQuestion,
      next: questionIndex === this.nextUnsolvedIndex,
    };

    if (!this.quizGameState || !this.quizGameState.userInput || !this.quizGameState.userInput[questionIndex]) {
      return classes;
    }

    classes.correct =
      this.quizGameState.userInput[questionIndex] === this.puzzleConfig.questions[questionIndex].solution;
    classes.filled = true;

    return classes;
  }

  async selectCurrectQuestion(i: number) {
    const indicatorClasses = this.getIndicatorClasses(i);
    if (indicatorClasses.current || (!indicatorClasses.filled && !indicatorClasses.next)) {
      return;
    }
    this.store.dispatch(new SetCurrentQuestion({ puzzleID: this.puzzleID, currentQuestion: i }));
  }

  async fastForward() {
    if (this.nextUnsolvedIndex !== -1) {
      this.store.dispatch(
        new SetCurrentQuestion({
          puzzleID: this.puzzleID,
          currentQuestion: this.nextUnsolvedIndex,
        })
      );
    } else if (!this.puzzleGame.finished) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
    } else {
      this.framework.pauseCurrentGame();
      const modal = await this.ionicModal.create({
        component: PuzzleWinDialogComponent,
        cssClass: 'puzzle-modal',
        backdropDismiss: false,
      });

      await modal.present();
      await modal.onDidDismiss();
      this.framework.resumeCurrentGame();

      // this.dialog.open(PuzzleWinDialogComponent, {
      //   closeOnNavigation: false,
      //   maxWidth: 800
      // });
    }
  }

  canGoFF() {
    const currentIndex = this.quizGameState.currentQuestion;
    return (
      this.quizGameState.userInput.filter((str) => !str).length === 0 ||
      (currentIndex + 2 < this.quizGameState.roundHashes.length &&
        this.quizGameState.userInput[currentIndex] &&
        this.quizGameState.userInput[currentIndex + 1])
    );
  }

  private async _winGame() {
    await this.selectAnswer(Math.floor(Math.random() * 4));
  }
}
